import { call, put, takeLatest } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'
import expertiseModel, { Expertise, CreateExpertiseDto, UpdateExpertiseDto, ExpertiseService } from '@/core/models/expertise.model'
import { SearchRequestOptions } from '@/core/types/request'

import {
  createExpertise as createExpertiseAction,
  createExpertiseSuccess as createExpertiseSuccessAction,
  createExpertiseFailure as createExpertiseFailureAction,
  createExternalServiceExpertise as createExternalServiceExpertiseAction,
  createExternalServiceExpertiseSuccess as createExternalServiceExpertiseSuccessAction,
  createExternalServiceExpertiseFailure as createExternalServiceExpertiseFailureAction,
  getExpertise as getExpertiseAction,
  getExpertiseSuccess as getExpertiseSuccessAction,
  getExpertiseFailure as getExpertiseFailureAction,
  getExpertises as getExpertisesAction,
  getExpertisesSuccess as getExpertisesSuccessAction,
  getExpertisesFailure as getExpertisesFailureAction,
  updateExpertise as updateExpertiseAction,
  updateExpertiseSuccess as updateExpertiseSuccessAction,
  updateExpertiseFailure as updateExpertiseFailureAction,
  deleteExpertise as deleteExpertiseAction,
  deleteExpertiseSuccess as deleteExpertiseSuccessAction,
  deleteExpertiseFailure as deleteExpertiseFailureAction,
  generateFSP as generateFSPAction,
  generateFSPSuccess as generateFSPSuccessAction,
  generateFSPFailure as generateFSPFailureAction,
} from '@/redux/reducers/expertise.reducer'

import { handleError } from '@/utils/errors'
import { Act } from '@/core/models/consultation.model'

function* createExpertise(action: PayloadAction<{ expertise: CreateExpertiseDto }>) {
  try {
    const expertise: Expertise = yield call(expertiseModel.createExpertise, action.payload.expertise)
    yield put(createExpertiseSuccessAction(expertise))
    // yield put(expertiseCreatedSocketEvent(expertise))
  } catch (exception) {
    console.warn(exception)
    yield put(createExpertiseFailureAction(handleError(exception)))
  }
}

function* createExternalServiceExpertise(action: PayloadAction<{ id: string; externalService: Exclude<ExpertiseService, ExpertiseService.MedConnect> }>) {
  try {
    const { id, externalService } = action.payload
    const expertise: Expertise = yield call(expertiseModel.createExternalServiceExpertise, id, externalService)
    yield put(createExternalServiceExpertiseSuccessAction(expertise))
  } catch (exception) {
    yield put(createExternalServiceExpertiseFailureAction(handleError(exception)))
  }
}

function* getExpertise(action: PayloadAction<string>) {
  try {
    const expertiseId = action.payload
    const expertise: Expertise = yield call(expertiseModel.getExpertise, expertiseId)
    yield put(getExpertiseSuccessAction(expertise))
  } catch (exception) {
    console.warn(exception)
    yield put(getExpertiseFailureAction(handleError(exception)))
  }
}

function* getExpertises(action: PayloadAction<{ query?: Record<string, any>; options?: SearchRequestOptions }>) {
  try {
    const { query, options } = action.payload
    const [expertises, totalCount]: [Expertise[], number] = yield call(expertiseModel.getExpertises, query, options)
    yield put(getExpertisesSuccessAction({ items: expertises, totalCount }))
  } catch (exception) {
    console.warn(exception)
    yield put(getExpertisesFailureAction(handleError(exception)))
  }
}

function* updateExpertise(action: PayloadAction<{ expertiseId: string; changes: UpdateExpertiseDto }>) {
  try {
    const { expertiseId, changes } = action.payload
    const expertise: Expertise = yield call(expertiseModel.updateExpertise, expertiseId, changes)
    yield put(updateExpertiseSuccessAction(expertise))
    // yield put(expertiseUpdatedSocketEvent(expertise))
  } catch (exception) {
    console.warn(exception)
    yield put(updateExpertiseFailureAction(handleError(exception)))
  }
}

function* deleteExpertise(action: PayloadAction<string>) {
  try {
    const expertiseId = action.payload
    yield call(expertiseModel.deleteExpertise, expertiseId)
    yield put(deleteExpertiseSuccessAction(expertiseId))
    // yield put(expertiseUpdatedSocketEvent(expertise))
  } catch (exception) {
    console.warn(exception)
    yield put(deleteExpertiseFailureAction(handleError(exception)))
  }
}

function* generateFSP(action: PayloadAction<{ expertiseId: string; acts: Act[] }>) {
  try {
    const { expertiseId, acts } = action.payload
    const expertise: Expertise = yield call(expertiseModel.getFSP, expertiseId, acts)
    yield put(getExpertiseSuccessAction(expertise))
    yield put(generateFSPSuccessAction())
  } catch (exception) {
    yield put(generateFSPFailureAction(handleError(exception)))
  }
}

const sagas = [
  takeLatest(createExpertiseAction.type, createExpertise),
  takeLatest(createExternalServiceExpertiseAction.type, createExternalServiceExpertise),
  takeLatest(getExpertiseAction.type, getExpertise),
  takeLatest(getExpertisesAction.type, getExpertises),
  takeLatest(updateExpertiseAction.type, updateExpertise),
  takeLatest(deleteExpertiseAction.type, deleteExpertise),
  takeLatest(generateFSPAction.type, generateFSP),
]

export default sagas
